import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import SEO from '../components/SEO';
import ProductCard from '../components/ProductCard';
import Filters from '../components/Filters';
import SortBy from '../components/SortBy';
import apiClient from '../utils/apiClient';
import '../styles/Products.css';

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({
    category: [],
    priceRange: 'all',
    rating: 'all',
  });
  const [sortBy, setSortBy] = useState('relevance');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('search') || '');
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const [isMobileSortOpen, setIsMobileSortOpen] = useState(false);

  // Update search query and category when URL params change
  useEffect(() => {
    const searchFromUrl = searchParams.get('search') || '';
    const categoryFromUrl = searchParams.get('category') || '';

    setSearchQuery(searchFromUrl);

    // Update filters based on URL category parameter
    if (categoryFromUrl) {
      setFilters(prev => ({
        ...prev,
        category: [categoryFromUrl]
      }));
    } else {
      // Clear category filter if no category param
      setFilters(prev => ({
        ...prev,
        category: []
      }));
    }
  }, [searchParams]);

  // Fetch products from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const params = new URLSearchParams();

        // Include category from URL in API request
        const categoryFromUrl = searchParams.get('category') || '';
        if (categoryFromUrl) {
          params.append('category', categoryFromUrl);
        }

        if (searchQuery.trim()) {
          params.append('search', searchQuery.trim());
        }
        const url = `/products${params.toString() ? `?${params.toString()}` : ''}`;
        const response = await apiClient.get(url);
        setProducts(response.data.products || []);
        setError(null);
      } catch (err) {
        console.error('Error fetching products:', err);
        setError('Failed to load products. Please try again.');
        // Fallback to empty array
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchQuery, searchParams]);

  const filteredAndSortedProducts = useMemo(() => {
    let filtered = [...products];

    // Apply category filter
    if (filters.category.length > 0) {
      filtered = filtered.filter((product) =>
        filters.category.includes(product.category)
      );
    }

    // Apply price filter
    if (filters.priceRange !== 'all') {
      filtered = filtered.filter((product) => {
        const [min, max] = filters.priceRange.split('-');
        const price = product.price;
        if (filters.priceRange === '10000-above') {
          return price >= 10000;
        }
        return price >= parseInt(min) && price <= parseInt(max);
      });
    }

    // Apply rating filter
    if (filters.rating !== 'all') {
      const rating = parseInt(filters.rating);
      filtered = filtered.filter((product) => product.rating >= rating);
    }

    // Apply sorting
    switch (sortBy) {
      case 'price-low':
        filtered.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        filtered.sort((a, b) => b.price - a.price);
        break;
      case 'newest':
        filtered.reverse();
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 4) - (a.rating || 4));
        break;
      case 'discount':
        filtered.sort((a, b) => (b.discount || 0) - (a.discount || 0));
        break;
      default:
        break;
    }

    return filtered;
  }, [filters, sortBy, products]);

  return (
    <div className="products-page">
      <SEO
        title={searchQuery ? `Search: ${searchQuery}` : 'Shop All Products'}
        description="Browse our collection of authentic handmade crafts, textiles, pottery, and home décor created by skilled artisans worldwide."
        canonical={`https://kalingastore.shop/products${searchQuery ? `?search=${encodeURIComponent(searchQuery)}` : ''}`}
      />
      <div className="products-container">
        <aside className="sidebar desktop-only">
          <Filters onFilterChange={setFilters} />
        </aside>

        {/* Mobile full-screen filters drawer */}
        {isMobileFiltersOpen && (
          <div className="mobile-filters-overlay" onClick={() => setIsMobileFiltersOpen(false)}>
            <div className="mobile-filters-panel" onClick={(e) => e.stopPropagation()}>
              <div className="mobile-filters-header">
                <span className="mobile-filters-title">FILTERS</span>
                <button
                  className="mobile-filters-clear"
                  onClick={() => {
                    const cleared = { category: [], priceRange: 'all', rating: 'all' };
                    setFilters(cleared);
                  }}
                >
                  CLEAR ALL
                </button>
              </div>
              <div className="mobile-filters-content-wrapper">
                <Filters isMobile={true} onFilterChange={(updated) => { setFilters(updated); }} />
              </div>
              <div className="mobile-filters-footer">
                <button className="mobile-filters-close" onClick={() => setIsMobileFiltersOpen(false)}>
                  CLOSE
                </button>
                <button className="mobile-filters-apply" onClick={() => setIsMobileFiltersOpen(false)}>
                  APPLY
                </button>
              </div>
            </div>
          </div>
        )}

        <main className="products-main">
          <div className="products-header">
            <h1>{searchQuery ? `Search Results for "${searchQuery}"` : 'All Products'}</h1>
            <p>Showing {filteredAndSortedProducts.length} products{searchQuery ? ` for "${searchQuery}"` : ''}</p>

            {/* Search bar for products page */}
            <div className="products-search">
              <form onSubmit={(e) => {
                e.preventDefault();
                const formData = new FormData(e.target);
                const query = formData.get('search').trim();
                if (query) {
                  setSearchParams({ search: query });
                } else {
                  setSearchParams({});
                }
              }}>
                <input
                  type="text"
                  name="search"
                  placeholder="Search products..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
                <button type="submit" className="search-btn">Search</button>
                {searchQuery && (
                  <button
                    type="button"
                    className="clear-search-btn"
                    onClick={() => {
                      setSearchQuery('');
                      setSearchParams({});
                    }}
                  >
                    Clear
                  </button>
                )}
              </form>

              {/* Popular search suggestions */}
              {!searchQuery && (
                <div className="search-suggestions">
                  <p>Popular searches:</p>
                  <div className="suggestion-tags">
                    {['saree', 'pottery', 'jewelry', 'handicrafts', 'textiles', 'decor'].map((term) => (
                      <button
                        key={term}
                        className="suggestion-tag"
                        onClick={() => setSearchParams({ search: term })}
                      >
                        {term}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="sort-container desktop-only">
            <SortBy onSortChange={setSortBy} />
          </div>

          {loading ? (
            <div className="loading-container">
              <div className="loading-spinner"></div>
              <p>Loading Products...</p>
            </div>
          ) : filteredAndSortedProducts.length > 0 ? (
            <div className="products-grid">
              {filteredAndSortedProducts.map((product) => (
                <ProductCard key={product.id} product={product} />
              ))}
            </div>
          ) : (
            <div className="no-products">
              <p>
                {searchQuery
                  ? `No products found for "${searchQuery}". Try different keywords or clear your search.`
                  : 'No products found matching your filters.'
                }
              </p>
            </div>
          )}
        </main>
      </div>

      {/* Bottom Sort / Filter bar for mobile */}
      <div className="mobile-sort-filter-bar">
        <button
          className="mobile-sort-btn"
          onClick={() => setIsMobileSortOpen(true)}
        >
          ↕ SORT
        </button>
        <button
          className="mobile-filter-btn"
          onClick={() => setIsMobileFiltersOpen(true)}
        >
          ≡ FILTER
        </button>
      </div>

      {/* Mobile Sort sheet */}
      {isMobileSortOpen && (
        <div className="mobile-sort-overlay" onClick={() => setIsMobileSortOpen(false)}>
          <div className="mobile-sort-sheet" onClick={(e) => e.stopPropagation()}>
            <div className="mobile-sort-header">
              <span>SORT BY</span>
            </div>
            <ul className="mobile-sort-list">
              <li className={sortBy === 'relevance' ? 'active' : ''} onClick={() => { setSortBy('relevance'); setIsMobileSortOpen(false); }}>
                <span className="sort-icon">🔥</span> Popularity
              </li>
              <li className={sortBy === 'newest' ? 'active' : ''} onClick={() => { setSortBy('newest'); setIsMobileSortOpen(false); }}>
                <span className="sort-icon">🆕</span> Latest
              </li>
              <li className={sortBy === 'discount' ? 'active' : ''} onClick={() => { setSortBy('discount'); setIsMobileSortOpen(false); }}>
                <span className="sort-icon">🏷️</span> Discount
              </li>
              <li className={sortBy === 'price-high' ? 'active' : ''} onClick={() => { setSortBy('price-high'); setIsMobileSortOpen(false); }}>
                <span className="sort-icon">₹</span> Price: High to Low
              </li>
              <li className={sortBy === 'price-low' ? 'active' : ''} onClick={() => { setSortBy('price-low'); setIsMobileSortOpen(false); }}>
                <span className="sort-icon">₹</span> Price: Low to High
              </li>
              <li className={sortBy === 'rating' ? 'active' : ''} onClick={() => { setSortBy('rating'); setIsMobileSortOpen(false); }}>
                <span className="sort-icon">⭐</span> Customer Rating
              </li>
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Products;
